/**
 * 设置页面
 *
 * 分组:
 *   - 基本:个人资料 / 手机号变更 / 实名认证
 *   - 数据:清除缓存
 *   - 隐私:隐私政策 / 服务条款 / 注销账户
 *   - 应用:字体大小 / 当前版本
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, User, Phone, ShieldCheck, Database,
  FileText, Trash2, Type, Info, AlertTriangle, Edit, Lock,
  Check, X, Loader2, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';

const FONT_SIZES = [
  { key: 'small', label: '小', size: '14px' },
  { key: 'standard', label: '标准', size: '15px' },
  { key: 'large', label: '大', size: '17px' },
  { key: 'xlarge', label: '特大', size: '19px' },
];

export default function SettingsPage() {
  const { user, setUser, logout } = useUser();
  const [editingName, setEditingName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [fontSize, setFontSize] = useState(localStorage.getItem('fucai3d_font_size') || 'standard');
  const [cacheSize, setCacheSize] = useState('0 MB');

  // 估算 localStorage 大小
  useEffect(() => {
    let total = 0;
    for (const k in localStorage) {
      if (localStorage.hasOwnProperty(k)) {
        total += (localStorage[k].length + k.length) * 2;
      }
    }
    if (total < 1024) setCacheSize(`${total} B`);
    else if (total < 1024 * 1024) setCacheSize(`${(total / 1024).toFixed(1)} KB`);
    else setCacheSize(`${(total / 1024 / 1024).toFixed(1)} MB`);
  }, []);

  // 应用字体大小
  useEffect(() => {
    const sz = FONT_SIZES.find(f => f.key === fontSize);
    if (sz) {
      document.documentElement.style.setProperty('--app-font-size', sz.size);
    }
  }, [fontSize]);

  const clearCache = () => {
    if (!confirm('清除缓存会:\n- 登出账号\n- 清除本地存储\n- 重新加载页面\n\n继续?')) return;
    localStorage.clear();
    toast.success('已清除缓存');
    setTimeout(() => window.location.reload(), 500);
  };

  return (
    <div className="min-h-screen bg-bg-base pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => window.history.back()} className="text-ink-1">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-base font-bold flex-1 text-center">设置</h1>
          <div className="w-6"></div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* 基本 */}
        <SettingsGroup title="基本">
          <SettingsItem
            icon={User}
            title="个人资料"
            subtitle={user?.name || user?.phone?.slice(-4) || '未设置'}
            onClick={() => setEditingName(true)}
          />
          <SettingsItem
            icon={Phone}
            title="手机号变更"
            subtitle={user?.phone ? `${user.phone.slice(0, 3)}****${user.phone.slice(-4)}` : '未登录'}
            onClick={() => setEditingPhone(true)}
          />
          <SettingsItem
            icon={ShieldCheck}
            title="实名认证"
            subtitle={user?.real_name_verified ? '✓ 已认证' : '未认证'}
            rightText={user?.real_name_verified ? undefined : '未认证'}
            rightColor={user?.real_name_verified ? 'text-brand-cyan' : 'text-ink-3'}
            onClick={() => toast.info('实名认证功能即将上线')}
          />
        </SettingsGroup>

        {/* 数据 */}
        <SettingsGroup title="数据">
          <SettingsItem
            icon={Database}
            title="清除缓存"
            rightText={cacheSize}
            rightColor="text-ink-3"
            onClick={clearCache}
          />
        </SettingsGroup>

        {/* 隐私 */}
        <SettingsGroup title="隐私">
          <SettingsItem
            icon={FileText}
            title="隐私政策"
            onClick={() => setShowPrivacy(true)}
          />
          <SettingsItem
            icon={FileText}
            title="服务条款"
            onClick={() => setShowTerms(true)}
          />
          <SettingsItem
            icon={Trash2}
            title="注销(删除账户)"
            titleColor="text-accent-danger"
            iconColor="text-accent-danger"
            onClick={() => setShowDelete(true)}
          />
        </SettingsGroup>

        {/* 应用 */}
        <SettingsGroup title="应用">
          <SettingsItem
            icon={Type}
            title="字体大小"
            rightText={FONT_SIZES.find(f => f.key === fontSize)?.label}
            rightColor="text-ink-3"
            onClick={() => {
              const idx = FONT_SIZES.findIndex(f => f.key === fontSize);
              const next = FONT_SIZES[(idx + 1) % FONT_SIZES.length];
              setFontSize(next.key);
              localStorage.setItem('fucai3d_font_size', next.key);
              toast.success(`字体大小:${next.label}`);
            }}
          />
          <SettingsItem
            icon={Info}
            title="当前版本"
            rightText="v2.6.0"
            rightColor="text-ink-3"
            onClick={() => toast.success('已是最新版本 ✨')}
          />
        </SettingsGroup>

        <div className="text-center text-[10px] text-ink-3 pt-4">
          ⚠ 仅供研究参考,理性投注
          <br />
          © 2026 3D MAX · 智能杀号专业版
        </div>
      </div>

      {/* 弹窗们 */}
      <AnimatePresence>
        {editingName && (
          <EditNameModal
            user={user}
            onClose={() => setEditingName(false)}
            onSuccess={(newName) => {
              setUser({ ...user, name: newName });
              // 同步到 localStorage
              const stored = JSON.parse(localStorage.getItem(`user_${myPhone}`) || '{}');
              stored.name = newName;
              localStorage.setItem(`user_${myPhone}`, JSON.stringify(stored));
              setEditingName(false);
            }}
          />
        )}
        {editingPhone && (
          <ChangePhoneModal
            currentPhone={user?.phone}
            onClose={() => setEditingPhone(false)}
            onSuccess={(newPhone) => {
              // 重新登录(token 失效)
              toast.success('手机号已更换,请重新登录');
              setTimeout(() => {
                logout();
                window.location.reload();
              }, 1500);
            }}
          />
        )}
        {showPrivacy && (
          <StaticPageModal
            title="隐私政策"
            onClose={() => setShowPrivacy(false)}
          >
            <PrivacyContent />
          </StaticPageModal>
        )}
        {showTerms && (
          <StaticPageModal
            title="服务条款"
            onClose={() => setShowTerms(false)}
          >
            <TermsContent />
          </StaticPageModal>
        )}
        {showDelete && (
          <DeleteAccountModal
            user={user}
            onClose={() => setShowDelete(false)}
            onSuccess={() => {
              logout();
              setTimeout(() => window.location.href = '/', 1000);
            }}
          />
        )}

        {/* 后端 API 地址切换器(本地测试用) */}
        <ApiUrlSwitcher />
      </AnimatePresence>
    </div>
  );
}

// ─── 小组件 ───
function ApiUrlSwitcher() {
  const [url, setUrl] = React.useState(() => {
    try { return localStorage.getItem('fucai3d_api_base') || ''; } catch { return ''; }
  });
  const [saved, setSaved] = React.useState(false);
  const presets = [
    { name: '生产 (workers.dev)', url: 'https://3d-max-api.mz18607358885.workers.dev' },
    { name: '本地 (wrangler dev)', url: 'http://localhost:8787' },
  ];
  const current = url || presets[0].url;
  return React.createElement('div', { className: 'card !p-3 mt-3' },
    React.createElement('div', { className: 'text-sm font-semibold mb-2 flex items-center gap-2' },
      React.createElement('span', null, '🔧'),
      ' 后端 API 地址'
    ),
    React.createElement('div', { className: 'text-[10px] text-ink-3 mb-2' },
      '默认生产地址;本地测试选 "localhost:8787"'
    ),
    React.createElement('div', { className: 'flex gap-1.5 mb-2' },
      ...presets.map(p =>
        React.createElement('button', {
          key: p.url,
          onClick: () => { setUrl(p.url); setSaved(false); },
          className: 'flex-1 px-2 py-1.5 rounded text-[10px] ' + (current === p.url ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2 border border-white/10'),
        }, p.name)
      )
    ),
    React.createElement('div', { className: 'flex gap-1.5' },
      React.createElement('input', {
        value: url,
        onChange: e => { setUrl(e.target.value); setSaved(false); },
        placeholder: 'https://xxx.workers.dev',
        className: 'flex-1 bg-bg-card border border-white/10 rounded px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-cyan/40',
      }),
      React.createElement('button', {
        onClick: () => {
          try { localStorage.setItem('fucai3d_api_base', url); setSaved(true); toast.success('✅ 已保存,刷新页面生效'); }
          catch (e) { toast.error('保存失败'); }
        },
        className: 'px-3 py-1.5 rounded text-xs bg-brand-cyan text-bg-base font-bold',
      }, '保存'),
      url && React.createElement('button', {
        onClick: () => { setUrl(''); try { localStorage.removeItem('fucai3d_api_base'); } catch {} setSaved(false); toast.success('已重置为默认'); },
        className: 'px-3 py-1.5 rounded text-xs bg-bg-elev text-ink-2 border border-white/10',
      }, '重置')
    ),
    saved && React.createElement('div', { className: 'text-[10px] text-brand-cyan mt-2' }, '✓ 已保存,刷新后生效')
  );
}

function SettingsGroup({ title, children }) {
  return (
    <div>
      <div className="text-xs text-ink-2 mb-2 px-2">{title}</div>
      <div className="glass rounded-2xl overflow-hidden divide-y divide-white/5">
        {children}
      </div>
    </div>
  );
}

function SettingsItem({ icon: Icon, title, subtitle, rightText, rightColor = 'text-ink-3', titleColor, iconColor, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/3 active:bg-white/5 transition text-left"
    >
      {Icon && <Icon size={18} className={clsx('flex-shrink-0', iconColor || 'text-ink-2')} />}
      <div className="flex-1 min-w-0">
        <div className={clsx('text-sm', titleColor || 'text-ink-0')}>{title}</div>
        {subtitle && <div className="text-xs text-ink-3 mt-0.5">{subtitle}</div>}
      </div>
      {rightText && (
        <span className={clsx('text-xs flex-shrink-0', rightColor)}>{rightText}</span>
      )}
      <ChevronRight size={16} className="text-ink-3 flex-shrink-0" />
    </button>
  );
}

// ─── 编辑昵称弹窗 ───
function EditNameModal({ user, onClose, onSuccess }) {
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const myPhone = user?.phone || '';

  const handleSave = async () => {
    if (!name.trim() || name.length > 30) {
      toast.error('昵称 1-30 字符');
      return;
    }
    setSaving(true);
    try {
      const res = await api.updateProfile({ name: name.trim() });
      if (res?.ok) {
        toast.success('已更新');
        onSuccess(name.trim());
      } else {
        toast.error(res?.error || '更新失败');
      }
    } catch (e) {
      // 离线兜底
      const stored = JSON.parse(localStorage.getItem(`user_${myPhone}`) || '{}');
      stored.name = name.trim();
      localStorage.setItem(`user_${myPhone}`, JSON.stringify(stored));
      toast.success('已更新(本地)');
      onSuccess(name.trim());
    }
    setSaving(false);
  };

  return (
    <Modal title="编辑昵称" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">昵称(1-30 字符)</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value.slice(0, 30))}
            placeholder="给自己起个名字"
            autoFocus
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand-cyan/40"
          />
          <div className="text-[10px] text-ink-3 mt-0.5 text-right">{name.length}/30</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-secondary">取消</button>
          <button onClick={handleSave} disabled={saving} className={clsx('btn-primary', saving && 'opacity-50')}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : '保存'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── 改手机号弹窗 ───
function ChangePhoneModal({ currentPhone, onClose, onSuccess }) {
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [devCode, setDevCode] = useState('');

  const phoneValid = /^1[3-9]\d{9}$/.test(newPhone);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSendCode = async () => {
    if (!phoneValid) {
      toast.error('手机号格式错误');
      return;
    }
    setSending(true);
    try {
      const res = await api.sendCode(newPhone);
      if (res?.ok) {
        toast.success('验证码已发送');
        setCountdown(60);
        if (res._devCode) setDevCode(res._devCode);
      } else {
        toast.error('发送失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
  };

  const handleSubmit = async () => {
    if (!phoneValid || code.length !== 6) {
      toast.error('请填写完整的手机号和验证码');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.changePhone(newPhone, code);
      if (res?.ok) {
        onSuccess(newPhone);
      } else {
        toast.error(res?.error || '更换失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  return (
    <Modal title="手机号变更" onClose={onClose}>
      <div className="space-y-3">
        <div className="rounded-lg bg-bg-card px-3 py-2 text-xs text-ink-2">
          当前手机号:<span className="text-brand-cyan font-mono">
            {currentPhone?.slice(0, 3)}****{currentPhone?.slice(-4)}
          </span>
        </div>
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">新手机号</label>
          <input
            type="tel"
            value={newPhone}
            onChange={e => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
            placeholder="11 位手机号"
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand-cyan/40"
          />
        </div>
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">验证码</label>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6 位"
              className="flex-1 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-center text-lg font-mono tracking-[0.3em] outline-none focus:border-brand-cyan/40"
            />
            <button
              onClick={handleSendCode}
              disabled={!phoneValid || sending || countdown > 0}
              className={clsx(
                'px-3 rounded-lg text-xs whitespace-nowrap',
                phoneValid && !sending && countdown === 0
                  ? 'btn-primary' : 'bg-white/5 text-ink-3'
              )}
            >
              {countdown > 0 ? `${countdown}秒` : (sending ? '发送中' : '发送验证码')}
            </button>
          </div>
          {devCode && (
            <div className="text-[10px] text-center text-ink-3 mt-1">
              🛠 开发模式验证码:<span className="text-brand-gold font-mono">{devCode}</span>
            </div>
          )}
        </div>
        <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5 text-xs text-amber-300">
          ⚠ 更换后需要重新登录
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-secondary">取消</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !phoneValid || code.length !== 6}
            className={clsx(
              'btn-primary',
              (submitting || !phoneValid || code.length !== 6) && 'opacity-50'
            )}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : '确认更换'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── 静态页(隐私政策/服务条款) ───
function StaticPageModal({ title, onClose, children }) {
  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="text-sm text-ink-1 leading-relaxed space-y-3 max-h-[60vh] overflow-y-auto">
        {children}
      </div>
    </Modal>
  );
}

function PrivacyContent() {
  return (
    <>
      <h3 className="text-base font-bold text-ink-0">隐私政策</h3>
      <p className="text-ink-3 text-xs">最近更新日期:2026 年 8 月 14 日 · 生效日期:2026 年 8 月 14 日</p>

      <p className="mt-3">
        欢迎使用 <strong>3D MAX</strong>(以下简称"本服务")。我们深知个人信息对您的重要性,并将按照法律法规要求,采取相应安全保护措施,致力于保护您的个人信息安全可控。鉴于此,本服务提供者(以下简称"我们")制定本《隐私政策》并提示:
        <strong className="text-brand-cyan">在使用本服务前,请您务必仔细阅读并透彻理解本政策,在确认充分理解并同意后再开始使用。</strong>
      </p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">一、我们如何收集和使用您的个人信息</h4>
      <p>我们仅会出于本政策所述的以下目的,收集和使用您的个人信息:</p>
      <div className="bg-white/3 rounded-lg p-2.5 text-xs space-y-1.5">
        <p><strong className="text-brand-cyan">(1) 注册/登录</strong>:当您注册本服务账号时,我们会收集您的<strong>手机号码</strong>用于身份验证,以及<strong>验证码</strong>用于登录核验。如您使用密码登录,我们会使用 PBKDF2-SHA256 算法(100,000 次迭代 + 16 字节随机盐值)对您的密码进行<strong>单向加密存储</strong>,我们不会以明文形式保存您的密码。</p>
        <p><strong className="text-brand-cyan">(2) 会员服务</strong>:当您购买会员时,我们会记录您的<strong>会员档位</strong>(Pro 月付 / Pro 年付 / MAX 买断)、<strong>有效期</strong>、<strong>支付金额</strong>及<strong>支付时间</strong>。</p>
        <p><strong className="text-brand-cyan">(3) 推荐分佣</strong>:如您使用推荐码邀请好友,我们会记录您与被邀请人之间的<strong>推荐关系</strong>(最多 3 级链式关系)、<strong>分佣金额</strong>及<strong>佣金状态</strong>。</p>
        <p><strong className="text-brand-cyan">(4) 服务使用</strong>:我们会记录您的<strong>登录时间</strong>、<strong>登录 IP</strong>、<strong>设备信息</strong>(浏览器类型、操作系统)及<strong>功能使用记录</strong>(选号、杀号、大底池生成),用于服务优化和安全保障。</p>
        <p><strong className="text-brand-cyan">(5) 客服沟通</strong>:当您联系客服或提交工单时,我们会保存您的<strong>沟通内容</strong>及<strong>客服评价</strong>。</p>
      </div>

      <h4 className="text-sm font-bold mt-4 text-ink-0">二、我们如何使用 Cookie 和同类技术</h4>
      <p>本服务使用 localStorage(本地存储)保存您的登录状态、字体偏好、杀号策略等设置。我们<strong>不会</strong>使用第三方追踪 Cookie 或跨站广告 Cookie。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">三、我们如何共享、转让、公开披露您的个人信息</h4>
      <p><strong>(1) 共享</strong>:我们<strong>不会</strong>与任何第三方公司、组织和个人共享您的个人信息,但以下情况除外:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>获得您的明确同意后</li>
        <li>根据法律法规、行政或司法强制要求</li>
        <li>为完成您请求的服务(如 SMS 短信发送需要第三方通信运营商)</li>
      </ul>
      <p><strong>(2) 转让</strong>:我们不会将您的个人信息转让给任何公司、组织和个人。</p>
      <p><strong>(3) 公开披露</strong>:我们不会公开披露您的个人信息,除非获得您的明确同意或法律法规要求。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">四、您的权利</h4>
      <p>您依法享有以下权利:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li><strong>访问</strong>:您可在"我的"页面查看您的账号信息</li>
        <li><strong>更正</strong>:您可在"设置 → 个人资料"修改昵称</li>
        <li><strong>删除</strong>:您可在"设置 → 注销账户"申请注销</li>
        <li><strong>更改手机号</strong>:您可在"设置 → 手机号变更"更换</li>
        <li><strong>撤回授权</strong>:您可清除浏览器缓存撤回 localStorage 授权</li>
      </ul>

      <h4 className="text-sm font-bold mt-4 text-ink-0">五、我们如何保护您的个人信息</h4>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>使用 HTTPS/TLS 加密所有数据传输</li>
        <li>使用 Cloudflare 全球边缘网络,具备 DDoS 防护</li>
        <li>数据库使用 D1(分布式 SQLite),具备自动备份</li>
        <li>敏感字段(password_hash)使用 PBKDF2 单向加密</li>
        <li>所有管理员操作需 X-Admin-Token 鉴权</li>
        <li>但请理解,任何互联网传输或存储都<strong>无法保证 100% 安全</strong></li>
      </ul>

      <h4 className="text-sm font-bold mt-4 text-ink-0">六、未成年人保护</h4>
      <p>本服务<strong className="text-accent-warn">不面向未满 18 周岁的未成年人</strong>。如您是未成年人,请<strong>立即停止使用本服务</strong>。我们不会主动收集未成年人信息,如发现将立即删除。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">七、本政策的修订</h4>
      <p>为给您提供更优质的服务,我们可能会适时修订本政策的条款。重大变更会通过应用内通知、短信等方式告知。如您不同意变更后的内容,可以选择停止使用本服务;继续使用则视为接受变更。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">八、联系我们</h4>
      <p>如您对本政策或您的个人信息保护有任何疑问、意见或建议,请通过以下方式联系我们:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>应用内:客服中心(常见问题 / 提交工单)</li>
        <li>商务合作:bd@3dmax.com</li>
        <li>我们会在 15 个工作日内回复您的请求</li>
      </ul>

      <div className="mt-4 text-xs text-ink-3 text-center">
        © 2026 3D MAX · 保留所有权利
      </div>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <h3 className="text-base font-bold text-ink-0">服务条款</h3>
      <p className="text-ink-3 text-xs">最近更新日期:2026 年 8 月 14 日 · 生效日期:2026 年 8 月 14 日</p>

      <p className="mt-3">
        <strong className="text-brand-cyan">请您仔细阅读本《服务条款》,在确认充分理解并同意全部条款后再开始使用 3D MAX。</strong>如您不同意本条款的任何内容,请立即停止使用并卸载本服务。
      </p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第一条 · 服务说明</h4>
      <p>3D MAX(以下简称"本服务")是一款基于机器学习(XGBoost)的<strong>彩票选号辅助工具</strong>,提供以下功能:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>多层大底池生成(4 层动态池子:L1=1000 / L2=500 / L3=300 / L4=100)</li>
        <li>杀号推荐(8 个传统公式 + 冷热号 + 遗漏检测)</li>
        <li>走势图分析(30/50/100/200/500 期)</li>
        <li>实战回测(滚动模拟)</li>
        <li>凯利公式下注计算</li>
        <li>三级推荐分佣</li>
        <li>在线客服</li>
      </ul>

      <h4 className="text-sm font-bold mt-4 text-accent-danger">第二条 · 重要声明(必读)</h4>
      <div className="rounded-lg border-2 border-accent-danger/30 bg-accent-danger/5 p-3 text-xs space-y-2">
        <p><strong>(1) 本服务仅供学习研究使用,所有数据和分析仅供参考,不构成任何投注建议。</strong></p>
        <p><strong>(2) 彩票投注在数学上具有负期望值,任何算法(包括但不限于机器学习、统计模型、专家经验)均<strong className="text-accent-danger">不能保证盈利</strong>。</strong></p>
        <p><strong>(3) 长期投注可能造成财产损失。请您务必<strong className="text-accent-danger">理性投注、量力而行、勿沉迷</strong>。</strong></p>
        <p><strong>(4) 本服务不向未满 18 周岁的未成年人提供服务。如您所在地区禁止彩票投注,请遵守当地法律法规。</strong></p>
        <p><strong>(5) 任何利用本服务进行违法违规活动的,我们将依法配合相关部门调查。</strong></p>
      </div>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第三条 · 用户行为规范</h4>
      <p>您承诺不得利用本服务从事以下行为:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>违反国家法律法规、危害网络安全的行为</li>
        <li>发布、传播违法、违规、淫秽、暴力、赌博等信息</li>
        <li>侵犯本服务或第三方知识产权、商业秘密等合法权益</li>
        <li>恶意刷号、套利、伪造推荐关系、虚假交易等</li>
        <li>对本服务进行反向工程、反编译、破解等</li>
        <li>利用技术漏洞获取不当利益</li>
        <li>其他违反公共秩序、善良风俗的行为</li>
      </ul>
      <p>如有违反,我们有权立即<strong>冻结账号、终止服务</strong>,并保留追究法律责任的权利。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第四条 · 会员制度</h4>
      <div className="bg-white/3 rounded-lg p-2.5 text-xs space-y-1.5">
        <p><strong>4.1 档位与价格</strong>:</p>
        <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2">
          <li>Pro 月付:¥169 / 30 天</li>
          <li>Pro 年付:¥699 / 365 天(省 70%)</li>
          <li>MAX 买断:¥1,999 / 永久</li>
        </ul>
        <p><strong>4.2 自动续费</strong>:Pro 月付/年付到期后会自动续费,您可随时在设置中关闭自动续费。</p>
        <p><strong>4.3 退款政策</strong>:虚拟服务一经开通<strong>不支持退款</strong>。如有质量问题(完全无法使用、严重 bug),可联系客服处理。</p>
        <p><strong>4.4 降级规则</strong>:从高级档位降级到低级档位需要明确确认,且原档位剩余天数<strong>作废</strong>。升级不退款,差额按比例计算补足。</p>
      </div>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第五条 · 推荐分佣规则</h4>
      <p>5.1 您可通过专属推荐码邀请好友,获得三级分佣奖励:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>1 级(直推):被邀请人充值金额的 <strong className="text-brand-gold">15%</strong></li>
        <li>2 级(间推):第二级被邀请人充值金额的 <strong className="text-brand-gold">10%</strong></li>
        <li>3 级(三推):第三级被邀请人充值金额的 <strong className="text-brand-gold">5%</strong></li>
        <li>最多可获 <strong>30%</strong> 分佣,3 级外无分佣</li>
      </ul>
      <p className="mt-2">5.2 <strong>只有付费用户</strong>(Pro 月付/年付/MAX)可获得分佣,试用用户不享受分佣权利。</p>
      <p>5.3 佣金进入"待结算"状态(7 天后转为"可提现"),最低 ¥100 可提现,支持微信/支付宝。</p>
      <p>5.4 <strong>严禁恶意刷号、虚假交易、自买自荐等套利行为</strong>,一经发现将冻结账号并清零佣金。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第六条 · 知识产权</h4>
      <p>本服务包含的所有内容(包括但不限于算法模型、公式、UI 设计、文字、图片、代码)的知识产权<strong>归我们所有</strong>,受著作权法、商标法等法律保护。未经授权,您不得:</p>
      <ul className="list-disc list-inside space-y-1 text-ink-2 ml-2 text-xs">
        <li>复制、修改、传播、销售本服务的任何内容</li>
        <li>对本服务进行反向工程、爬取数据</li>
        <li>利用本服务开发竞品</li>
      </ul>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第七条 · 免责声明</h4>
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 text-xs space-y-1.5 text-ink-1">
        <p><strong>7.1</strong> 本服务<strong>不保证</strong>所提供信息的准确性、完整性、可靠性、及时性。基于本服务进行的任何投注决策及其后果由<strong>您本人承担</strong>。</p>
        <p><strong>7.2</strong> 因不可抗力(包括但不限于自然灾害、网络中断、电信故障、黑客攻击、政府行为等)导致的服务中断或数据丢失,我们<strong>不承担责任</strong>。</p>
        <p><strong>7.3</strong> 因您的设备故障、网络问题、操作失误等自身原因造成的损失,我们<strong>不承担责任</strong>。</p>
        <p><strong>7.4</strong> 因第三方原因(如支付渠道、银行、SMS 服务商)导致的服务异常,我们会尽力协调但<strong>不承担赔偿责任</strong>。</p>
      </div>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第八条 · 责任限制</h4>
      <p>在法律允许的最大范围内,本服务对您的全部责任<strong>不超过您最近 12 个月内为本服务支付的金额</strong>。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第九条 · 服务变更与终止</h4>
      <p>9.1 我们保留随时修改、暂停或终止本服务的权利,重大变更会提前 7 天通知。</p>
      <p>9.2 您可随时停止使用本服务。如需注销账号,请前往"设置 → 注销账户"。</p>
      <p>9.3 如您违反本条款,我们有权立即终止服务,已支付费用<strong>不予退还</strong>。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第十条 · 法律适用与争议解决</h4>
      <p>本条款的解释、效力及争议解决均适用<strong>中华人民共和国法律</strong>。如发生争议,双方应友好协商;协商不成的,任何一方均有权将争议提交<strong>本服务运营方所在地有管辖权的人民法院</strong>通过诉讼方式解决。</p>

      <h4 className="text-sm font-bold mt-4 text-ink-0">第十一条 · 其他</h4>
      <p>11.1 本条款的任何条款被认定为无效或不可执行,不影响其他条款的效力。</p>
      <p>11.2 本条款的最终解释权归本服务运营方所有。</p>

      <div className="mt-4 text-xs text-ink-3 text-center">
        © 2026 3D MAX · 保留所有权利
      </div>
    </>
  );
}

// ─── 注销账户弹窗 ───
function DeleteAccountModal({ user, onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (confirm !== 'DELETE') {
      toast.error('确认文字必须输入 DELETE');
      return;
    }
    if (!password) {
      toast.error('请输入密码');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.deleteAccount(password, confirm);
      if (res?.ok) {
        toast.success('账户已注销');
        onSuccess();
      } else {
        toast.error(res?.error || '注销失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  return (
    <Modal title="注销账户" onClose={onClose}>
      <div className="space-y-3">
        <div className="rounded-lg bg-accent-danger/10 border border-accent-danger/30 p-3 text-xs text-accent-danger space-y-1">
          <div className="font-bold flex items-center gap-1">
            <AlertTriangle size={14} />
            ⚠ 危险操作
          </div>
          <ul className="list-disc list-inside space-y-0.5 text-ink-1">
            <li>账户将进入 30 天冷静期</li>
            <li>期间可联系客服恢复</li>
            <li>30 天后数据彻底删除</li>
            <li>推荐关系、佣金记录、杀号策略全部清空</li>
          </ul>
        </div>
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">输入密码以确认</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="您的登录密码"
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent-danger/40"
          />
        </div>
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">输入 <span className="text-accent-danger font-mono">DELETE</span> 以确认</label>
          <input
            type="text"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="DELETE"
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono outline-none focus:border-accent-danger/40"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-secondary">取消</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || confirm !== 'DELETE' || !password}
            className={clsx(
              'rounded-lg py-2.5 text-sm font-semibold transition',
              (submitting || confirm !== 'DELETE' || !password)
                ? 'bg-white/5 text-ink-3 cursor-not-allowed'
                : 'bg-accent-danger text-white hover:bg-accent-danger/90'
            )}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : '确认注销'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── 通用 Modal ───
function Modal({ title, children, onClose, wide }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className={clsx(
          'w-full bg-bg-2 rounded-t-2xl md:rounded-2xl border border-white/10 p-5',
          wide ? 'md:max-w-2xl max-h-[90vh] overflow-y-auto' : 'md:max-w-md'
        )}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold">{title}</h3>
          <button onClick={onClose} className="text-ink-3 hover:text-ink-0">
            <X size={20} />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
